import { destroyDiInstance, diInject } from '@di/di';
import { CreativeMode } from '@domain/creative/environment';
import { VideoSizeMode } from '@domain/video';
import { cloneDeep } from '@studio/utils/clone';
import { getVideoErrorMessage, VideoError } from '@studio/utils/errors';
import { ErrorMessage } from '@studio/utils/errors/error-message.enum';
import { isBannerflow, isBase64Mp4Video, isRelativeUrl, replaceOrigin } from '@studio/utils/url';
import { deepEqual } from '@studio/utils/utils';
import { isValidUrl } from '@studio/utils/validation';
import { StreamManager } from './video-streamer/stream-manager';
export class VideoRenderer {
    get _isPlaying() {
        return !this._videoDOMElement.paused;
    }
    get _startTime() {
        var _a;
        let animatorTime = this._animator.time;
        if (this._env.MODE === CreativeMode.ImageGenerator) {
            animatorTime = (_a = this._renderer.creativeDocument.startTime) !== null && _a !== void 0 ? _a : 0;
        }
        return Math.max(animatorTime - this._viewElement.time, 0) + this._settings.startTime;
    }
    get _endTime() {
        if (!this._settings.endTime) {
            return undefined;
        }
        return Math.min(this._viewElement.time, this.duration_m) + this._settings.endTime;
    }
    get duration_m() {
        return this._videoDOMElement.duration;
    }
    get videoDOMElement_m() {
        return this._videoDOMElement;
    }
    constructor(_env, _viewElement) {
        this._env = _env;
        this._viewElement = _viewElement;
        this._destroyed = false;
        this._reachedTrackingPoints = new Set();
        this._ad = diInject(0 /* Token.AD */);
        this._renderer = diInject(1 /* Token.RENDERER */);
        this._feedStore = diInject(4 /* Token.FEED_STORE */, { optional: true });
        this._animator = diInject(3 /* Token.ANIMATOR */);
        this._onPlaybackButtonClick = (event) => {
            if (this._renderer.isEditorMode) {
                return;
            }
            event.stopPropagation();
            event.preventDefault();
            if (!this._isPlaying) {
                this._userInitiatedPlaybackState = 'play';
                this._play();
            }
            else {
                this._userInitiatedPlaybackState = 'pause';
                this._pause();
            }
        };
        this._onAnimatorPlay = () => {
            var _a;
            this._userInitiatedPlaybackState = undefined;
            if (!this._settings.autoplay) {
                return;
            }
            const animatorTime = ((_a = this._animator) === null || _a === void 0 ? void 0 : _a.time) || 0;
            if (animatorTime > this._viewElement.time) {
                this._play();
            }
        };
        this._onAnimationStart = (element) => {
            if (this._dataElement.id !== element.id) {
                return;
            }
            if (!this._settings.autoplay) {
                if (!this._userInitiatedPlaybackState || this._userInitiatedPlaybackState === 'pause') {
                    return;
                }
            }
            if (!this._settings.loop && this._settings.restartWithCreative) {
                this._loop();
                this._play();
            }
            else if (this._settings.loop) {
                this._play();
            }
        };
        this._onAnimationLoop = () => {
            if (this._settings.restartWithCreative) {
                this._loop();
            }
            if (this._dataElement.feed) {
                this._setVideoSrc();
                this._play();
            }
        };
        this._onAnimationEnd = (element) => {
            if (this._dataElement.id !== element.id) {
                return;
            }
            this._pause();
        };
        this._onAnimatorSeek = () => {
            if (!this._videoDOMElement) {
                return;
            }
            this._setStartTime();
        };
        this._onRendererMute = (muted) => {
            if (!this._videoDOMElement) {
                return;
            }
            this._videoDOMElement.muted = muted;
        };
        this._play = () => {
            if (this._isPlaying) {
                return;
            }
            this._videoDOMElement.play();
            this._setPlaybackButtonState();
            this._requestId = requestAnimationFrame(this._tick);
        };
        this._pause = () => {
            const continueLooping = !this._settings.stopWithCreative && this._settings.loop;
            if (continueLooping && this._env.MODE === CreativeMode.AnimatedCreative) {
                return;
            }
            this._videoDOMElement.pause();
            this._setPlaybackButtonState();
            cancelAnimationFrame(this._requestId);
        };
        this._stop = () => {
            if (this._settings.stopWithCreative) {
                this._pause();
            }
        };
        this._onVideoEnd = () => {
            if (!this._isPlaying) {
                return;
            }
            if (this._settings.loop) {
                this._loop();
                this._play();
            }
            else {
                this._pause();
            }
        };
        this._setStartTime = () => {
            this._videoDOMElement.currentTime = this._startTime;
        };
        this._tick = () => {
            if (this._destroyed) {
                return;
            }
            this._trackVideoPlayback();
            if (this._endTime && this._videoDOMElement.currentTime >= this._endTime) {
                this._onVideoEnd();
            }
            this._requestId = requestAnimationFrame(this._tick);
        };
        this._dataElement = _viewElement.__data;
        this._settings = Object.assign(Object.assign({}, this._dataElement.videoSettings), { url: this._getVideoUrl() });
        if (!this._animator) {
            throw new Error('Animator not provided.');
        }
        this._animator.on('play', this._onAnimatorPlay);
        this._animator.on('pause', this._pause);
        this._animator.on('stop', this._stop);
        this._animator.on('loop', this._onAnimationLoop);
        this._animator.on('animation_start', this._onAnimationStart);
        this._animator.on('animation_end', this._onAnimationEnd);
        this._animator.on('seek', this._onAnimatorSeek);
        this._renderer.on('mute', this._onRendererMute);
        this._init();
    }
    async _init() {
        await this._createVideoDOMElement();
        this.updateVideo(true);
    }
    async _createVideoDOMElement() {
        var _a, _b;
        const videoRenderer = document.createElement('video-renderer');
        this._shadowRoot = videoRenderer.attachShadow({ mode: 'open' });
        videoRenderer.style.width = '100%';
        videoRenderer.style.height = '100%';
        videoRenderer.style.display = 'block';
        const video = document.createElement('video');
        video.preload = 'auto';
        this._setMuted(video);
        video.setAttribute('playsinline', '');
        video.style.pointerEvents = 'none';
        this._videoDOMElement = video;
        this._setStartTime();
        this._preparePreloading();
        this._setVideoStyles();
        try {
            await this._initVideoSource();
        }
        catch (e) {
            const error = e;
            throw new VideoError(`${ErrorMessage.VideoSourceInitFailed}: ${error.message}`);
        }
        this._shadowRoot.appendChild(this._videoDOMElement);
        const foreignObject = (_a = this._viewElement.__rootElement) === null || _a === void 0 ? void 0 : _a.querySelector('foreignObject');
        (_b = foreignObject === null || foreignObject === void 0 ? void 0 : foreignObject.firstElementChild) === null || _b === void 0 ? void 0 : _b.appendChild(videoRenderer);
        this._createPlaybackButtons();
    }
    _setVideoStyles() {
        const style = document.createElement('style');
        style.textContent = `
            video {
                width: 100%;
                height: 100%;
                max-width: 100%;
                max-height: 100%;
                object-fit: contain;
                object-position: center center;
                pointer-events: none;
                left: 50%;
                top: 50%;
                position: absolute;
                transform: translate(-50%, -50%);
            }

            video.cropMode {
                width: 100%;
                height: 100%;
                object-fit: cover;
            }

            .playbackButton {
                transform: scale(var(--playbackButtonSize));
            }

            .play, .pause {
                width: 100%;
                height: 100%;
                position: absolute;
                fill: var(--playbackButtonColor);
                top: 0;
                left: 0;
            }

            .play {
                display: var(--playbackButtonPlayState)
            }

            .pause {
                display: var(--playbackButtonPauseState)
            }
        `;
        this._shadowRoot.appendChild(style);
    }
    _createPlaybackButtons() {
        this._videoPlaybackButtons = document.createElement('div');
        this._videoPlaybackButtons.className = 'playbackButton';
        this._videoPlaybackButtons.style.position = 'absolute';
        this._videoPlaybackButtons.style.display = 'none';
        this._videoPlaybackButtons.style.top = '0px';
        this._videoPlaybackButtons.style.left = '0px';
        this._videoPlaybackButtons.style.width = '100%';
        this._videoPlaybackButtons.style.height = '100%';
        this._videoPlaybackButtons.style.objectFit = 'cover';
        this._videoPlaybackButtons.style.objectPosition = 'center center';
        this._videoPlaybackButtons.addEventListener('click', this._onPlaybackButtonClick);
        this._videoPlaybackButtons.innerHTML = playbackButtonHtml;
        this._shadowRoot.appendChild(this._videoPlaybackButtons);
    }
    _preparePreloading() {
        let videoPromise;
        const videoLoadPromise = new Promise((resolve, reject) => {
            videoPromise = {
                resolve,
                reject
            };
        });
        this._renderer.preloadingElements.set(this._dataElement.id, videoLoadPromise);
        const onVideoCanPlaythrough = () => {
            videoPromise.resolve();
            this._videoDOMElement.removeEventListener('canplaythrough', onVideoCanPlaythrough);
        };
        this._videoDOMElement.addEventListener('canplaythrough', onVideoCanPlaythrough);
        const onVideoError = () => {
            var _a, _b;
            const videoError = new VideoError(getVideoErrorMessage((_a = this._videoDOMElement.error) === null || _a === void 0 ? void 0 : _a.code), {
                src: this._videoDOMElement.src,
                code: (_b = this._videoDOMElement.error) === null || _b === void 0 ? void 0 : _b.code
            });
            videoPromise.reject(videoError);
            this._videoDOMElement.removeEventListener('error', onVideoError);
            this.destroy();
        };
        this._videoDOMElement.addEventListener('error', onVideoError);
        this._videoDOMElement.addEventListener('ended', this._onVideoEnd);
    }
    updateVideo(initialRender) {
        if ((this._isPlaying || this._animator.isPlaying) && !initialRender) {
            return;
        }
        for (const videoSetting in this._settings) {
            const setting = videoSetting;
            if (!initialRender &&
                deepEqual(this._settings[setting], this._dataElement.videoSettings[setting])) {
                continue;
            }
            const settingValue = this._getElementSettingValue(setting);
            this._settings[setting] = cloneDeep(settingValue);
            /**
             * The autoplay attribute always has to be unset as it instead
             * has to be controlled from a timeline standpoint through
             * the animator events
             **/
            if (setting === 'autoplay') {
                continue;
            }
            switch (setting) {
                case 'startTime':
                case 'endTime':
                    this._setStartTime();
                    break;
                case 'url':
                    this._setVideoSrc();
                    break;
                case 'sizeMode':
                    this._setSizeMode();
                    break;
                case 'playbackButton':
                    this._setPlaybackButton();
                    break;
                default:
                    this._videoDOMElement[setting] = settingValue;
                    break;
            }
        }
        if (this._startTime !== this._videoDOMElement.currentTime) {
            this._setStartTime();
        }
    }
    _setMuted(video) {
        video.muted =
            this._env.MODE !== CreativeMode.VideoGenerator &&
                this._env.MODE !== CreativeMode.DesignView;
    }
    _setPlaybackButton() {
        const { color, size, enabled } = this._settings.playbackButton;
        const style = this._videoPlaybackButtons.style;
        style.display = enabled || size === 0 ? 'block' : 'none';
        style.setProperty(PlaybackButtonVariable.Color, color.toString());
        style.setProperty(PlaybackButtonVariable.Size, String(size / 100));
        this._setPlaybackButtonState();
    }
    _setPlaybackButtonState() {
        const style = this._videoPlaybackButtons.style;
        const playButtonVisible = this._isPlaying ? 'none' : 'block';
        const pauseButtonVisible = !this._isPlaying ? 'none' : 'block';
        style.setProperty(PlaybackButtonVariable.PlayState, playButtonVisible);
        style.setProperty(PlaybackButtonVariable.PauseState, pauseButtonVisible);
    }
    _getVideoUrl() {
        var _a;
        if (this._dataElement.feed) {
            const feedValue = (_a = this._feedStore) === null || _a === void 0 ? void 0 : _a.getFeedValueUrl(this._dataElement.feed, this._dataElement.id, this._dataElement);
            return (feedValue !== null && feedValue !== void 0 ? feedValue : '').trim();
        }
        const customOrigin = this._env.CUSTOM_AD_DOMAIN;
        if (customOrigin && this._dataElement.videoAsset) {
            return replaceOrigin(this._dataElement.videoAsset.url, customOrigin).trim();
        }
        return this._dataElement.videoAsset.url.trim();
    }
    _getElementSettingValue(setting) {
        if (setting === 'url') {
            return this._getVideoUrl();
        }
        return this._dataElement.videoSettings[setting];
    }
    _setSizeMode() {
        const mode = this._dataElement.videoSettings.sizeMode;
        switch (mode) {
            case VideoSizeMode.Fit:
                this._videoDOMElement.classList.remove('cropMode');
                break;
            case VideoSizeMode.Crop:
                this._videoDOMElement.classList.add('cropMode');
                break;
        }
    }
    async _initVideoSource() {
        const shouldStream = !isBannerflow(window.location.href) && this._dataElement.videoSettings.streaming.enabled;
        if (shouldStream) {
            try {
                await this._setVideoStream();
            }
            catch (_a) {
                delete this._streamManager;
                console.warn(`Failed to stream video, falling back to: ${this._getVideoUrl()}`);
                this._setVideoSrc();
            }
        }
        else {
            this._setVideoSrc();
        }
    }
    async _setVideoStream() {
        this._streamManager = new StreamManager(this._videoDOMElement, this._settings.url);
        this._videoDOMElement.src = await this._streamManager.loadStreams_m();
    }
    _setVideoSrc() {
        const url = this._getVideoUrl();
        if (this._streamManager || this._videoDOMElement.src === url || !this._isSrcValid(url)) {
            return;
        }
        this._videoDOMElement.src = url;
        this._videoDOMElement.load();
    }
    _isSrcValid(url) {
        if (!url) {
            return false;
        }
        if (isRelativeUrl(url)) {
            return true;
        }
        const { pathname, hostname, href: src } = new URL(url);
        const isStorageEmulatorUrl = hostname === 'storage-emulator';
        const isBase64VideoUrl = isBase64Mp4Video(pathname);
        return !(isStorageEmulatorUrl && isValidUrl(src) && isBase64VideoUrl);
    }
    _loop() {
        this._setStartTime();
    }
    _trackVideoPlayback() {
        var _a, _b;
        const { loop, restartWithCreative } = this._settings;
        /**
         * Video tracking quartiles are based on the longest possible playtime of the video.
         * It's only allowed to span over multiple loops iTf the duration of the element is the same
         * as the entire creative as the video will contiously play across the loops and always be visible.
         * Otherwise it's based on the shortest viable playtime.
         */
        const endTime = (_a = this._endTime) !== null && _a !== void 0 ? _a : Number.MAX_SAFE_INTEGER;
        const shortestPlaytime = this._dataElement.duration === this._animator.duration && loop && !restartWithCreative
            ? Math.min(endTime, this.duration_m)
            : Math.min(this._dataElement.duration, endTime, this.duration_m);
        const percentilePlayed = this._videoDOMElement.currentTime / shortestPlaytime;
        const url = this._settings.url;
        const filename = (_b = this._dataElement.videoAsset) === null || _b === void 0 ? void 0 : _b.name;
        let progressValue = 0;
        // 95% is considered a complete playthrough
        if (percentilePlayed >= 0.95 && !this._reachedTrackingPoints.has(95)) {
            progressValue = 1;
            this._reachedTrackingPoints.add(95);
        }
        else if (percentilePlayed >= 0.75 && !this._reachedTrackingPoints.has(75)) {
            progressValue = 0.75;
            this._reachedTrackingPoints.add(75);
        }
        else if (percentilePlayed >= 0.5 && !this._reachedTrackingPoints.has(50)) {
            progressValue = 0.5;
            this._reachedTrackingPoints.add(50);
        }
        else if (percentilePlayed >= 0.25 && !this._reachedTrackingPoints.has(25)) {
            progressValue = 0.25;
            this._reachedTrackingPoints.add(25);
        }
        if (progressValue > 0) {
            this._ad.tracking.trackCustomProgressEvent({
                name: 'Video completion',
                id: url,
                label: filename,
                value: progressValue
            });
        }
    }
    destroy() {
        var _a, _b, _c, _d, _e, _f, _g, _h;
        (_a = this._animator) === null || _a === void 0 ? void 0 : _a.off('play', this._onAnimatorPlay);
        (_b = this._animator) === null || _b === void 0 ? void 0 : _b.off('pause', this._pause);
        (_c = this._animator) === null || _c === void 0 ? void 0 : _c.off('stop', this._stop);
        (_d = this._animator) === null || _d === void 0 ? void 0 : _d.off('loop', this._onAnimationLoop);
        (_e = this._animator) === null || _e === void 0 ? void 0 : _e.off('animation_start', this._onAnimationStart);
        (_f = this._animator) === null || _f === void 0 ? void 0 : _f.off('animation_end', this._onAnimationEnd);
        (_g = this._animator) === null || _g === void 0 ? void 0 : _g.off('seek', this._onAnimatorSeek);
        (_h = this._renderer) === null || _h === void 0 ? void 0 : _h.off('mute', this._onRendererMute);
        this._videoDOMElement.removeEventListener('ended', this._onVideoEnd);
        cancelAnimationFrame(this._requestId);
        this._destroyed = true;
        destroyDiInstance(this);
    }
}
var PlaybackButtonVariable;
(function (PlaybackButtonVariable) {
    PlaybackButtonVariable["Color"] = "--playbackButtonColor";
    PlaybackButtonVariable["Size"] = "--playbackButtonSize";
    PlaybackButtonVariable["PlayState"] = "--playbackButtonPlayState";
    PlaybackButtonVariable["PauseState"] = "--playbackButtonPauseState";
})(PlaybackButtonVariable || (PlaybackButtonVariable = {}));
const playbackButtonHtml = `
<svg class="play" xmlns="http://www.w3.org/2000/svg" x="0px" y="0px" width="100%" height="100%" viewBox="0 0 640 640" xml:space="preserve">
<g>
    <path d="M546.273 93.726C485.834 33.286 405.476 0 320 0 234.525 0 154.166 33.286 93.726 93.726S0 234.525 0 320c0 85.476 33.286 165.834 93.726 226.273C154.166 606.714 234.524 640 320 640s165.835-33.286 226.273-93.727C606.714 485.835 640 405.476 640 320s-33.286-165.834-93.727-226.274zM320 582C175.533 582 58 464.467 58 320 58 175.533 175.533 58 320 58c144.467 0 262 117.533 262 262 0 144.467-117.533 262-262 262z"/>
    <path d="M243.289 320V188.698L357 254.35 470.711 320 357 385.651l-113.711 65.651z"/>
</g>
</svg>
<svg class="pause" xmlns="http://www.w3.org/2000/svg" x="0px" y="0px" width="100%" height="100%" viewBox="0 0 640 640" xml:space="preserve">
<g>
    <path d="M546.273 93.726C485.834 33.286 405.476 0 320 0 234.525 0 154.166 33.286 93.726 93.726S0 234.525 0 320c0 85.476 33.286 165.834 93.726 226.273C154.166 606.714 234.524 640 320 640s165.835-33.286 226.273-93.727C606.714 485.835 640 405.476 640 320s-33.286-165.834-93.727-226.274zM320 582C175.533 582 58 464.467 58 320 58 175.533 175.533 58 320 58c144.467 0 262 117.533 262 262 0 144.467-117.533 262-262 262z"/>
    <path d="M375 192.854h52.486v254.291H375zM215 192.854h52.486v254.291H215z"/>
</g>
</svg>`;
